import { type LogtoConfig, UserScope } from '@logto/react';
import { openLink, retrieveLaunchParams } from '@tma.js/sdk-react';

import { env } from '@/env';

// Qriots audience constants — declared before logtoConfig so they can be
// referenced in the resources array and anywhere else in this file.
const QRIOTS_ACCESS = 'qlash.qriots_access_token';
const QRIOTS_EXP = 'qlash.qriots_token_exp';
const QRIOTS_RESOURCE = 'https://qriots.qlash.gg/api';

// QBot audience — same resource as the backoffice, scoped storage keys.
const QBOT_ACCESS = 'qlash.qbot_access_token';
const QBOT_EXP = 'qlash.qbot_token_exp';
const QBOT_RESOURCE = 'https://qbot.qlash.gg';

/**
 * Logto OIDC configuration for the QLASH Telegram Mini App.
 *
 * Values mirror the mobile-app feat/new-logto branch (src/services/logto.ts):
 * the same staging tenant, app, and API resource — so the access token issued
 * by Logto authenticates against the existing Laravel backend (which validates
 * via JWKS). Production values are picked up from the env when VITE_LOGTO_*
 * vars are set; defaults fall back to staging.
 */
export const logtoConfig: LogtoConfig = {
  endpoint: env.logtoEndpoint,
  appId: env.logtoAppId,
  resources: [env.logtoResource, QRIOTS_RESOURCE, QBOT_RESOURCE],
  scopes: [
    UserScope.CustomData,
    UserScope.Email,
    UserScope.Identities,
    UserScope.Profile,
    UserScope.Roles,
    'offline_access',
  ],
};

export const API_RESOURCE = env.logtoResource;

const SCOPE_STRING = [
  'openid',
  'offline_access',
  'profile',
  'email',
  UserScope.CustomData,
  UserScope.Identities,
  UserScope.Roles,
].join(' ');

/* ------------------------------------------------------------------ */
/* Manually-managed token storage (Telegram external-browser flow)    */
/* ------------------------------------------------------------------ */
//
// Google blocks OAuth in embedded WebViews ("disallowed_useragent"). To work
// around it we run the OIDC flow in the user's *system* browser via
// `WebApp.openLink`, then bridge the auth code back via a `t.me` deep link
// with the code in `start_param`. The mini-app then exchanges the code for
// tokens manually and stores them under these keys. RTK Query's
// `prepareHeaders` reads from this storage first, falling back to the Logto
// SDK if no manual token is present (covers the standalone web flow).

const QLASH_ACCESS = 'qlash.access_token';
const QLASH_REFRESH = 'qlash.refresh_token';
const QLASH_EXP = 'qlash.token_exp';
const PKCE_VERIFIER = 'qlash.pkce.verifier';
const PKCE_STATE = 'qlash.pkce.state';
const BRIDGE_DEBUG = 'qlash.bridge_debug';
const BACKEND_TOKEN = 'qlash.backend_token';

export function getBridgeDebug(): string | null {
  return localStorage.getItem(BRIDGE_DEBUG);
}
export function clearBridgeDebug(): void {
  localStorage.removeItem(BRIDGE_DEBUG);
}
function dbg(msg: string): void {
  console.info('[bridge]', msg);
  const prev = localStorage.getItem(BRIDGE_DEBUG) || '';
  const stamp = new Date().toISOString().slice(11, 19);
  const next = `${prev}${stamp} ${msg}\n`;
  localStorage.setItem(BRIDGE_DEBUG, next.slice(-2000));
}

interface TokenSet {
  access_token: string;
  refresh_token?: string;
  expires_in: number;
}

function persistTokens(tokens: TokenSet): void {
  localStorage.setItem(QLASH_ACCESS, tokens.access_token);
  if (tokens.refresh_token) localStorage.setItem(QLASH_REFRESH, tokens.refresh_token);
  const expAt = Math.floor(Date.now() / 1000) + tokens.expires_in;
  localStorage.setItem(QLASH_EXP, String(expAt));
}

export function clearStoredTokens(): void {
  localStorage.removeItem(QLASH_ACCESS);
  localStorage.removeItem(QLASH_REFRESH);
  localStorage.removeItem(QLASH_EXP);
  localStorage.removeItem(PKCE_VERIFIER);
  localStorage.removeItem(PKCE_STATE);
  localStorage.removeItem(BACKEND_TOKEN);
  localStorage.removeItem(QRIOTS_ACCESS);
  localStorage.removeItem(QRIOTS_EXP);
  localStorage.removeItem(QBOT_ACCESS);
  localStorage.removeItem(QBOT_EXP);
  // Also wipe Logto SDK's own localStorage state so the SDK re-evaluates as
  // unauthenticated on next render. Without this, `sdkAuthenticated` stays
  // true after logout and the AppShell gate continues rendering the app.
  for (const key of Object.keys(localStorage)) {
    if (key.startsWith('logto:')) localStorage.removeItem(key);
  }
}

export function hasStoredAccessToken(): boolean {
  const tok = localStorage.getItem(QLASH_ACCESS);
  const exp = parseInt(localStorage.getItem(QLASH_EXP) ?? '0', 10);
  return !!tok && exp - 60 > Math.floor(Date.now() / 1000);
}

/* ------------------------------------------------------------------ */
/* PKCE helpers                                                       */
/* ------------------------------------------------------------------ */

function base64UrlEncode(bytes: Uint8Array): string {
  let str = '';
  for (const b of bytes) str += String.fromCharCode(b);
  return btoa(str).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function randomString(length: number): string {
  const bytes = new Uint8Array(length);
  crypto.getRandomValues(bytes);
  return base64UrlEncode(bytes).slice(0, length);
}

async function sha256(input: string): Promise<Uint8Array> {
  const data = new TextEncoder().encode(input);
  const buf = await crypto.subtle.digest('SHA-256', data);
  return new Uint8Array(buf);
}

/* ------------------------------------------------------------------ */
/* Bridge between Logto SDK and our token storage                      */
/* ------------------------------------------------------------------ */

let accessTokenGetter:
  | ((resource?: string) => Promise<string | undefined>)
  | null = null;

export function setAccessTokenGetter(
  getter: (resource?: string) => Promise<string | undefined>,
): void {
  accessTokenGetter = getter;
}

export async function getLogtoAccessToken(): Promise<string | null> {
  // Prefer manually-stored token (Telegram bridge flow).
  if (hasStoredAccessToken()) {
    return localStorage.getItem(QLASH_ACCESS);
  }
  // Stored token expired — try the refresh_token (also stored in bridge flow).
  // Refreshing with `resource` returns a fresh JWT scoped to the API resource.
  const refreshToken = localStorage.getItem(QLASH_REFRESH);
  if (refreshToken) {
    const refreshed = await refreshLogtoToken();
    if (refreshed) return refreshed;
  }
  // Fall back to Logto SDK (standalone web flow).
  if (!accessTokenGetter) return null;
  try {
    const token = await accessTokenGetter(API_RESOURCE);
    return token ?? null;
  } catch (err) {
    console.warn('[logto] getAccessToken failed:', err);
    return null;
  }
}

/* ------------------------------------------------------------------ */
/* Laravel backend token exchange                                     */
/* ------------------------------------------------------------------ */
//
// The Laravel API (api.tournaments.qlash.gg) does NOT accept Logto JWTs
// directly. Calling /tournaments with a raw Logto Bearer token returns a
// stale/cached response (looks like data from an unauthed/legacy code path).
// Mobile-app fixes this by exchanging the Logto token for a Laravel-issued
// token via POST /auth/exchange-token, then using *that* token everywhere.
// We mirror the flow here.

interface ExchangeResponse {
  access_token?: string;
  token?: string;
}

export function getBackendToken(): string | null {
  return localStorage.getItem(BACKEND_TOKEN);
}

export function clearBackendToken(): void {
  localStorage.removeItem(BACKEND_TOKEN);
}

/**
 * Refreshes the Logto access token using the stored refresh token, with the
 * `resource` parameter so Logto returns a JWT scoped to the API resource
 * (not an opaque token). Use this when the cached access_token may be opaque
 * or expired, before calling the backend exchange.
 */
async function refreshLogtoToken(): Promise<string | null> {
  const refreshToken = localStorage.getItem(QLASH_REFRESH);
  if (!refreshToken) return null;
  const tokenUrl = new URL('oidc/token', env.logtoEndpoint).toString();
  const body = new URLSearchParams({
    grant_type: 'refresh_token',
    refresh_token: refreshToken,
    client_id: env.logtoAppId,
    resource: env.logtoResource,
  });
  try {
    const res = await fetch(tokenUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body,
    });
    if (!res.ok) {
      console.warn('[logto] refresh failed', res.status, await res.text().catch(() => ''));
      return null;
    }
    const tokens = (await res.json()) as TokenSet;
    if (tokens.access_token) {
      persistTokens(tokens);
      return tokens.access_token;
    }
    return null;
  } catch (err) {
    console.warn('[logto] refresh network error', err);
    return null;
  }
}

/* ------------------------------------------------------------------ */
/* Qriots-scoped access token                                          */
/* ------------------------------------------------------------------ */
//
// Qriots-scoped access token (audience: https://qriots.qlash.gg/api).
// Cached in localStorage like the main token. Refresh on demand using
// the shared refresh_token; no separate auth flow needed.

function hasFreshQriotsToken(): boolean {
  const tok = localStorage.getItem(QRIOTS_ACCESS);
  const exp = parseInt(localStorage.getItem(QRIOTS_EXP) ?? '0', 10);
  return !!tok && exp - 60 > Math.floor(Date.now() / 1000);
}

function persistQriotsToken(tokens: TokenSet): void {
  if (tokens.refresh_token) localStorage.setItem(QLASH_REFRESH, tokens.refresh_token);
  localStorage.setItem(QRIOTS_ACCESS, tokens.access_token);
  const expAt = Math.floor(Date.now() / 1000) + tokens.expires_in;
  localStorage.setItem(QRIOTS_EXP, String(expAt));
}

/**
 * QBot-scoped access token (audience: https://qbot.qlash.gg).
 * Used by qbotApi.ts. Same caching pattern as the qriots variant.
 */
function hasFreshQbotToken(): boolean {
  const tok = localStorage.getItem(QBOT_ACCESS);
  const exp = parseInt(localStorage.getItem(QBOT_EXP) ?? '0', 10);
  return !!tok && exp - 60 > Math.floor(Date.now() / 1000);
}

function persistQbotToken(tokens: TokenSet): void {
  if (tokens.refresh_token) localStorage.setItem(QLASH_REFRESH, tokens.refresh_token);
  localStorage.setItem(QBOT_ACCESS, tokens.access_token);
  const expAt = Math.floor(Date.now() / 1000) + tokens.expires_in;
  localStorage.setItem(QBOT_EXP, String(expAt));
}

export async function getQbotAccessToken(): Promise<string | null> {
  if (hasFreshQbotToken()) {
    return localStorage.getItem(QBOT_ACCESS);
  }
  const refreshToken = localStorage.getItem(QLASH_REFRESH);
  if (!refreshToken) return null;

  const tokenUrl = new URL('oidc/token', env.logtoEndpoint).toString();
  const body = new URLSearchParams({
    grant_type: 'refresh_token',
    refresh_token: refreshToken,
    client_id: env.logtoAppId,
    resource: QBOT_RESOURCE,
  });
  try {
    const res = await fetch(tokenUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body,
    });
    if (!res.ok) {
      console.warn('[logto-qbot] refresh failed', res.status, await res.text().catch(() => ''));
      return null;
    }
    const tokens = (await res.json()) as TokenSet;
    if (tokens.access_token) {
      persistQbotToken(tokens);
      return tokens.access_token;
    }
    return null;
  } catch (err) {
    console.warn('[logto-qbot] refresh network error', err);
    return null;
  }
}

/**
 * Returns an access token whose `aud` claim is `https://qriots.qlash.gg/api`.
 * Used by qriotsApi.ts. Returns null if there's no refresh_token (user
 * not signed in) or if Logto rejects the refresh (resource not authorized).
 */
export async function getQriotsAccessToken(): Promise<string | null> {
  if (hasFreshQriotsToken()) {
    return localStorage.getItem(QRIOTS_ACCESS);
  }
  const refreshToken = localStorage.getItem(QLASH_REFRESH);
  if (!refreshToken) return null;

  const tokenUrl = new URL('oidc/token', env.logtoEndpoint).toString();
  const body = new URLSearchParams({
    grant_type: 'refresh_token',
    refresh_token: refreshToken,
    client_id: env.logtoAppId,
    resource: QRIOTS_RESOURCE,
  });
  try {
    const res = await fetch(tokenUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body,
    });
    if (!res.ok) {
      console.warn('[logto-qriots] refresh failed', res.status, await res.text().catch(() => ''));
      return null;
    }
    const tokens = (await res.json()) as TokenSet;
    if (tokens.access_token) {
      persistQriotsToken(tokens);
      return tokens.access_token;
    }
    return null;
  } catch (err) {
    console.warn('[logto-qriots] refresh network error', err);
    return null;
  }
}

function decodeJwtPart(part: string): Record<string, unknown> | null {
  try {
    const norm = part.replace(/-/g, '+').replace(/_/g, '/');
    const padded = norm + '='.repeat((4 - (norm.length % 4)) % 4);
    return JSON.parse(atob(padded));
  } catch {
    return null;
  }
}
function decodeJwtPayload(token: string): Record<string, unknown> | null {
  const parts = token.split('.');
  if (parts.length < 2) return null;
  return decodeJwtPart(parts[1]);
}
function decodeJwtHeader(token: string): Record<string, unknown> | null {
  const parts = token.split('.');
  if (parts.length < 2) return null;
  return decodeJwtPart(parts[0]);
}

async function postExchange(token: string): Promise<string | null> {
  const exchangeUrl = `${env.apiUrl}/auth/exchange-token`;
  const tokenKind = token.startsWith('eyJ') ? 'JWT' : 'opaque';
  if (tokenKind === 'JWT') {
    const header = decodeJwtHeader(token);
    const claims = decodeJwtPayload(token);
    if (header) dbg(`JWT header: ${JSON.stringify(header)}`);
    if (claims) {
      dbg(`JWT payload: ${JSON.stringify(claims)}`);
    } else {
      dbg('JWT decode failed');
    }
  }
  dbg(`exchange-token POST kind=${tokenKind} len=${token.length}`);
  try {
    const res = await fetch(exchangeUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        platform: 'telegram',
      },
      body: JSON.stringify({ token }),
    });
    if (!res.ok) {
      const errBody = await res.text().catch(() => '');
      dbg(`exchange-token FAIL ${res.status} ${errBody.slice(0, 150)}`);
      return null;
    }
    const data = (await res.json()) as ExchangeResponse;
    const backendToken = data.access_token ?? data.token ?? null;
    dbg(`exchange-token OK got_token=${!!backendToken}`);
    return backendToken;
  } catch (err) {
    dbg(`exchange-token NETWORK_ERR ${err instanceof Error ? err.message : String(err)}`);
    return null;
  }
}

export async function exchangeLogtoForBackend(): Promise<string | null> {
  const initial = await getLogtoAccessToken();
  if (!initial) return null;

  // First try with the cached Logto access token.
  let backendToken = await postExchange(initial);

  // If the cached token is opaque/stale, refresh it (with `resource`) to get
  // a fresh JWT, then retry the exchange.
  if (!backendToken) {
    const refreshed = await refreshLogtoToken();
    if (refreshed) backendToken = await postExchange(refreshed);
  }

  if (backendToken) {
    localStorage.setItem(BACKEND_TOKEN, backendToken);
    return backendToken;
  }
  return null;
}

/**
 * Returns the cached Laravel backend token if present, otherwise performs the
 * Logto→backend exchange and caches the result.
 */
export async function getApiAccessToken(): Promise<string | null> {
  const cached = getBackendToken();
  if (cached) return cached;
  return exchangeLogtoForBackend();
}

/* ------------------------------------------------------------------ */
/* Telegram-aware sign-in                                             */
/* ------------------------------------------------------------------ */

export function getRedirectUri(): string {
  return `${window.location.origin}/auth/callback`;
}

/**
 * True when running inside a Telegram WebView (mobile/desktop client) — i.e.
 * `Telegram.WebApp.initData` is populated. Returns false in plain browser
 * (where the standard Logto redirect flow works).
 */
export function isTelegramWebView(): boolean {
  try {
    const lp = retrieveLaunchParams();
    return !!lp.tgWebAppData?.user;
  } catch {
    return false;
  }
}

/**
 * Builds the Logto OIDC authorize URL with PKCE state stored locally for the
 * subsequent token exchange.
 */
async function buildAuthUrl(): Promise<string> {
  const verifier = randomString(64);
  const state = randomString(32);
  const challenge = base64UrlEncode(await sha256(verifier));

  localStorage.setItem(PKCE_VERIFIER, verifier);
  localStorage.setItem(PKCE_STATE, state);

  const url = new URL('oidc/auth', env.logtoEndpoint);
  url.searchParams.set('client_id', env.logtoAppId);
  url.searchParams.set('redirect_uri', getRedirectUri());
  url.searchParams.set('response_type', 'code');
  url.searchParams.set('scope', SCOPE_STRING);
  // Request both resources at sign-in so the issued refresh_token is
  // authorised for both audiences. Logto allows multiple `resource` params
  // (RFC 8707); empirically verified 2026-05-22 that this does not affect
  // redirect_uri validation. Without the qriots audience here, the refresh
  // grant in getQriotsAccessToken() returns 400.
  url.searchParams.append('resource', env.logtoResource);
  url.searchParams.append('resource', QRIOTS_RESOURCE);
  url.searchParams.append('resource', QBOT_RESOURCE);
  url.searchParams.set('code_challenge', challenge);
  url.searchParams.set('code_challenge_method', 'S256');
  url.searchParams.set('state', state);
  url.searchParams.set('prompt', 'consent');
  return url.toString();
}

/**
 * Telegram-side sign-in: opens Logto in the user's system browser, where
 * Google/Apple OAuth aren't blocked. The bridge in <AuthCallback> then
 * deep-links back to the bot with the auth code in `start_param`.
 */
export async function startTelegramSignIn(): Promise<void> {
  const url = await buildAuthUrl();
  dbg(`signIn: openLink → ${url.slice(0, 80)}...`);
  const result = openLink.ifAvailable(url);
  dbg(`signIn: openLink.ifAvailable returned ${JSON.stringify(result)}`);
}

/**
 * Exchanges an authorization code received via `start_param` (after the
 * external-browser → t.me deep-link bridge) for tokens, and stores them.
 */
export async function exchangeAuthCode(code: string): Promise<boolean> {
  const verifier = localStorage.getItem(PKCE_VERIFIER);
  if (!verifier) {
    dbg('exchange FAIL: PKCE verifier missing');
    return false;
  }

  const tokenUrl = new URL('oidc/token', env.logtoEndpoint).toString();
  const redirectUri = getRedirectUri();
  const body = new URLSearchParams({
    grant_type: 'authorization_code',
    code,
    code_verifier: verifier,
    redirect_uri: redirectUri,
    client_id: env.logtoAppId,
    // RFC 8707 resource indicator — needed for Logto to issue a JWT scoped to
    // the API resource. Without it, access_token comes back opaque and our
    // Laravel /auth/exchange-token rejects it ("Token verification failed").
    // The qriots-scoped token is NOT requested here by design; it is minted
    // on-demand via getQriotsAccessToken() using the same refresh_token.
    resource: env.logtoResource,
  });

  dbg(`exchange POST ${tokenUrl} code=${code.slice(0, 10)}.. ver_len=${verifier.length} redir=${redirectUri}`);

  let res: Response;
  try {
    res = await fetch(tokenUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body,
    });
  } catch (err) {
    dbg(`exchange FAIL: network/CORS ${err instanceof Error ? err.message : String(err)}`);
    return false;
  }

  if (!res.ok) {
    const errBody = await res.text().catch(() => '<unreadable>');
    dbg(`exchange FAIL: HTTP ${res.status} ${errBody.slice(0, 200)}`);
    return false;
  }

  const tokens = (await res.json()) as TokenSet;
  if (!tokens.access_token) {
    dbg('exchange FAIL: no access_token in response');
    return false;
  }
  dbg(`exchange OK: token_len=${tokens.access_token.length} expires_in=${tokens.expires_in}`);

  // We've issued an `openid offline_access` request but Logto returns the
  // *opaque* access token only when no `resource` is specified. Since we passed
  // `resource`, this token is the JWT scoped to the API resource — exactly
  // what RTK Query needs.
  persistTokens(tokens);

  localStorage.removeItem(PKCE_VERIFIER);
  localStorage.removeItem(PKCE_STATE);
  return true;
}

/**
 * Reads the `auth_<code>` start_param when the mini-app is reopened via the
 * t.me deep-link bridge, exchanges the code for tokens, and returns true on
 * success. Returns null if no auth start_param is present.
 */
export async function consumeAuthStartParam(): Promise<boolean | null> {
  let startParam: string | undefined;
  try {
    startParam = retrieveLaunchParams().tgWebAppStartParam;
  } catch (err) {
    dbg(`consume FAIL: retrieveLaunchParams threw ${err instanceof Error ? err.message : String(err)}`);
    return null;
  }
  if (!startParam) {
    dbg('consume: no tgWebAppStartParam present');
    return null;
  }
  if (!startParam.startsWith('auth_')) {
    dbg(`consume: startParam is not auth_*: ${startParam.slice(0, 30)}`);
    return null;
  }
  const code = startParam.slice('auth_'.length);
  return await exchangeAuthCode(code);
}
